import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Package,
  DollarSign,
  FileText,
  AlertCircle,
  Globe,
  Shield,
  MessageSquare,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { VisuallyHidden } from '../ui/visually-hidden';
import { motion } from 'motion/react';
import { markAsRead, type NotificationItem } from '@/services/notificationService';
import { getNotificationColor, formatNotificationTime } from './notifications-constants';

const getCategoryIcon = (category: string) => {
  switch (category) {
    case 'order':
      return Package;
    case 'payment':
      return DollarSign;
    case 'task':
      return FileText;
    case 'system':
      return AlertCircle;
    case 'site':
      return Globe;
    case 'dispute':
      return Shield;
    case 'ticket':
      return MessageSquare;
    default:
      return Bell;
  }
};

const getActionUrl = (notification: NotificationItem): string | null => {
  if (notification.data?.action_url) return notification.data.action_url;
  const { type, data } = notification;
  if (type.startsWith('order_') && data?.order_id) return `/orders/${data.order_id}`;
  if (type.startsWith('deposit_') || type.startsWith('withdrawal_') || type.startsWith('refund_'))
    return '/wallet';
  if (type.startsWith('payment_method_')) return '/wallet/payment-methods';
  if (type.startsWith('tax_profile_')) return '/settings/tax-profile';
  if (type.startsWith('ticket_')) return '/help-center';
  if (type.startsWith('dispute_') && data?.order_id) return `/orders/${data.order_id}`;
  if (type.startsWith('account_') || type.startsWith('profile_')) return '/profile';
  return null;
};

interface NotificationsPanelProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  notifications: NotificationItem[];
  onNotificationRead: (id: string) => void;
  trigger?: React.ReactNode;
}

export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  isOpen,
  onOpenChange,
  notifications,
  onNotificationRead,
  trigger,
}) => {
  const navigate = useNavigate();
  const unreadCount = notifications.length;

  const handleNotificationClick = async (notification: NotificationItem) => {
    onNotificationRead(notification.id);
    onOpenChange(false);

    // Mark as read in background
    markAsRead(notification.id).catch(() => {});

    const url = getActionUrl(notification);
    if (url) {
      void navigate(url);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      {trigger && <PopoverTrigger asChild>{trigger}</PopoverTrigger>}
      <PopoverContent
        align="end"
        className="w-[420px] p-0 border border-[#E1E6EC] shadow-xl"
        style={{
          width: '420px',
          padding: '0',
          borderRadius: '12px',
          border: '1px solid #E1E6EC',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        }}
        sideOffset={8}
      >
        <VisuallyHidden>
          <p id="notifications-description">
            You have {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}.
          </p>
        </VisuallyHidden>

        {/* Header */}
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid #E1E6EC',
            background: 'linear-gradient(135deg, #F9FAFC 0%, #F3F4F6 100%)',
          }}
        >
          <div className="flex items-center justify-between" style={{ gap: '8px' }}>
            <div>
              <h3
                className="text-base font-semibold"
                style={{ color: '#1A202C', lineHeight: '1.3' }}
              >
                Notifications
              </h3>
              <p className="text-xs font-normal" style={{ color: '#4C5157', marginTop: '2px' }}>
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                onOpenChange(false);
                void navigate('/notifications');
              }}
              className="text-[11px] font-medium"
              style={{ height: '28px', padding: '0 8px', color: '#283673' }}
            >
              View all
            </Button>
          </div>
        </div>

        {/* Notifications List */}
        <ScrollArea className="h-[400px]">
          <div style={{ padding: '8px 16px' }}>
            {unreadCount === 0 ? (
              <div style={{ paddingTop: '32px', paddingBottom: '32px', textAlign: 'center' }}>
                <div
                  className="mx-auto flex items-center justify-center"
                  style={{
                    width: '48px',
                    height: '48px',
                    marginBottom: '12px',
                    borderRadius: '12px',
                    backgroundColor: '#F0F3FF',
                  }}
                >
                  <Bell className="w-6 h-6" style={{ color: '#283673' }} />
                </div>
                <h3
                  className="text-sm font-semibold"
                  style={{ color: '#1A202C', marginBottom: '4px' }}
                >
                  No new notifications
                </h3>
                <p className="text-xs" style={{ color: '#717182', lineHeight: '1.3' }}>
                  You're all caught up!
                </p>
              </div>
            ) : (
              <div className="space-y-1" style={{ gap: '4px' }}>
                {notifications.map((notification, index) => {
                  const Icon = getCategoryIcon(notification.category);
                  const colors = getNotificationColor(notification.category);

                  return (
                    <motion.div
                      key={notification.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                    >
                      <div
                        className="group rounded-lg border cursor-pointer transition-all bg-[#F0F4FF] hover:bg-[#E8EFFF]"
                        style={{
                          borderRadius: '8px',
                          border: `1px solid ${colors.border}20`,
                          padding: '10px',
                          transition: 'all 0.2s ease',
                        }}
                        onClick={() => void handleNotificationClick(notification)}
                      >
                        <div className="flex items-start" style={{ gap: '8px' }}>
                          {/* Icon */}
                          <div
                            className="flex-shrink-0 flex items-center justify-center"
                            style={{
                              backgroundColor: colors.bg,
                              width: '32px',
                              height: '32px',
                              borderRadius: '8px',
                            }}
                          >
                            <Icon className="w-4 h-4" style={{ color: colors.text }} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div
                              className="flex items-start justify-between"
                              style={{ gap: '8px', marginBottom: '2px' }}
                            >
                              <h4
                                className="text-xs font-semibold flex items-center"
                                style={{ color: '#1A202C', lineHeight: '1.4', gap: '6px' }}
                              >
                                {notification.title}
                                <span
                                  style={{
                                    backgroundColor: colors.text,
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                  }}
                                />
                              </h4>
                            </div>

                            <p
                              className="text-[11px] font-normal"
                              style={{ color: '#4C5157', lineHeight: '1.4', marginBottom: '4px' }}
                            >
                              {notification.message}
                            </p>

                            <span className="text-[10px] font-normal" style={{ color: '#717182' }}>
                              {formatNotificationTime(notification.created_at)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
};
